import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

import { PointOfSaleService } from '../../../../../services/pos';
import { DeviceDetectorService } from '../../../../../services/device-detector.service';
import { AccountTypeService } from '../../../../../services/account-type';
import { WarehouseTypeService } from '../../../../../services/warehouse-type';
import { UserService } from '../../../../../services/user';

@Component({
  selector: 'app-pospagemobile',
  templateUrl: './pospagemobile.component.html',
  styleUrl: './pospagemobile.component.scss'
})
export class PospagemobileComponent implements OnInit {

  allData: any[] = [];

  contentReady = true;
  isLoadingMore = false;
  noData = false;

  page = 1;
  totalPages = 1;
  isMobile = false;
  isAr = localStorage.getItem('language') === 'ar';
  userId: any = null;

  selectedPosDetails: any = null;
  detailsPopup = false;

  constructor(
    private router: Router,
    private posService: PointOfSaleService,
    private accountTypeService: AccountTypeService,
    private warehouseTypeService: WarehouseTypeService,
    private userService: UserService,
    private deviceService: DeviceDetectorService
  ) { }

  // ===============================
  // INIT
  // ===============================

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    if (!this.deviceService.isMobileDevice()) {
      this.router.navigateByUrl('/Pospage');
      return;
    }

    this.accountTypeService.getAccountType().subscribe({
      next: () => this.loadData(),
      error: () => this.loadData()
    });
  }

  // ===============================
  // ROLE GETTERS
  // ===============================

  get isAdmin(): boolean {
    return this.accountTypeService.isAdmin() ||
      this.accountTypeService.isSuperAdmin();
  }

  get isSubAdmin(): boolean {
    return this.accountTypeService.isSubAdmin();
  }

  get superAdmin(): boolean {
    return this.accountTypeService.isSuperAdmin();
  }

  get isblueboxMain(): boolean {
    return this.warehouseTypeService.isBlueBoxMain;
  }

  get activePosId(): string | null {
    return localStorage.getItem('posId');
  }

  get hasSingleWarehouse(): boolean {
    return this.allData.length <= 1;
  }

  // ===============================
  // LOAD DATA
  // ===============================

  loadData(): void {
    this.contentReady = true;
    this.isLoadingMore = false;
    this.noData = false;

    this.page = 1;
    this.totalPages = 1;
    this.allData = [];

    if (this.superAdmin) {
      this.loadAllPOS();
      return;
    }

    this.getUserIdAndLoadPOS();
  }

  getUserIdAndLoadPOS(): void {
    this.userService.getUserId().subscribe({
      next: (data) => {
        this.userId = data?.user_id ?? localStorage.getItem('user_id');

        if (!this.userId) {
          this.finishEmptyState();
          return;
        }

        this.loadUserPOS();
      },
      error: () => {
        this.userId = localStorage.getItem('user_id');

        if (!this.userId) {
          this.finishEmptyState();
          return;
        }

        this.loadUserPOS();
      }
    });
  }

  loadAllPOS(): void {
    this.posService.getAllPOS(this.page).subscribe({
      next: (res) => {
        this.allData = res?.data ?? [];
        this.totalPages = res?.last_page ?? 1;

        this.setDefaultActivePos();

        this.contentReady = false;
        this.noData = this.allData.length === 0;
      },
      error: () => {
        this.contentReady = false;
      }
    });
  }

  loadUserPOS(): void {
    this.posService.getUserPOS(this.userId).subscribe({
      next: (data) => {
        this.allData = data ?? [];
        this.totalPages = 1;

        this.setDefaultActivePos();

        this.contentReady = false;
        this.noData = this.allData.length === 0;
      },
      error: () => {
        this.contentReady = false;
      }
    });
  }

  finishEmptyState(): void {
    this.allData = [];
    this.noData = true;
    this.contentReady = false;
    this.isLoadingMore = false;
  }

  // ===============================
  // INFINITE SCROLL
  // ===============================

  @HostListener('window:scroll', [])
  onScroll(): void {
    if (!this.superAdmin) return;
    if (this.contentReady || this.isLoadingMore) return;
    if (this.page >= this.totalPages) return;

    const bottomReached =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 120;

    if (!bottomReached) return;

    this.loadMorePOS();
  }

  loadMorePOS(): void {
    this.page++;
    this.isLoadingMore = true;

    this.posService.getAllPOS(this.page).subscribe({
      next: (res) => {
        const newData = res?.data ?? [];

        this.allData = [...this.allData, ...newData];
        this.totalPages = res?.last_page ?? this.totalPages;

        this.isLoadingMore = false;
      },
      error: () => {
        this.page--;
        this.isLoadingMore = false;
      }
    });
  }

  // ===============================
  // ACTIVE POS
  // ===============================

  setDefaultActivePos(): void {
    if (!this.allData.length) return;

    const current = localStorage.getItem('posId');

    const currentExists = this.allData.some(
      item => String(item?.id) === String(current)
    );

    if (!current || !currentExists) {
      const activeId = this.allData[0].id;
      localStorage.setItem('posId', String(activeId));
      this.warehouseTypeService.getWarehouseType(Number(activeId)).subscribe();
    }
  }

  isActive(element: any): boolean {
    return String(this.activePosId) === String(element?.id);
  }

  // ===============================
  // ACTIONS
  // ===============================

  showProducts(id: string): void {
    this.clearCartForWarehouseChange();
    localStorage.setItem('posId', String(id));
    this.navigateAndReload(['/Home.mobile']);
  }

  createOrder(id: string): void {
    this.clearCartForWarehouseChange();
    localStorage.setItem('posId', String(id));
    this.navigateAndReload(['/Addorder']);
  }

  openAppointments(warehouseId: string | number): void {
    this.router.navigate([this.isMobile ? '/clinic-appointments.mobile' : '/clinic-appointments'], {
      queryParams: { warehouse_id: warehouseId }
    });
  }

  canManageAppointments(element: any): boolean {
    return !this.isSubAdmin && ['2', '3', '4'].includes(String(element?.warehouses_type));
  }

  edit(element: any): void {
    this.router.navigate(['/createpos'], { queryParams: this.buildEditQueryParams(element) });
  }

  private buildEditQueryParams(element: any): any {
    const settings = element?.business_setting || element?.businessSetting || element?.settings || {};

    return {
      ...element,
      edit_price: settings?.edit_price ?? element?.edit_price,
      quantityguard: settings?.quantityguard ?? element?.quantityguard
    };
  }

  deleteitem(id: string): void {
    Swal.fire({
      title: this.isAr ? 'متأكد؟' : 'Are You Sure?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'NO'
    }).then(result => {
      if (!result.isConfirmed) return;

      this.contentReady = true;

      this.posService.deletePOS(id).subscribe({
        next: () => {
          if (String(this.activePosId) === String(id)) {
            localStorage.removeItem('posId');
          }

          this.loadData();
        },
        error: () => {
          this.contentReady = false;
        }
      });
    });
  }

  // ===============================
  // DETAILS POPUP
  // ===============================

  openDetailsPopup(element: any): void {
    this.selectedPosDetails = element;
    this.detailsPopup = true;
  }

  closeDetailsPopup(): void {
    this.detailsPopup = false;
    this.selectedPosDetails = null;
  }

  // ===============================
  // HELPERS
  // ===============================

  private clearCartForWarehouseChange(): void {
    localStorage.removeItem('cart');
    localStorage.removeItem('cartWarehouseId');
  }

  private navigateAndReload(commands: any[]): void {
    this.router.navigate(commands).then((navigated) => {
      if (navigated) {
        window.location.reload();
      }
    });
  }

  getPosName(element: any): string {
    if (!element) return '-';

    if (this.isAr) {
      return element.name || element.name_en || '-';
    }

    return element.name_en || element.name || '-';
  }

  warehouseTypeLabel(type: any): string {
    switch (String(type)) {
      case '0':
        return 'company';
      case '1':
        return 'warehouse';
      case '2':
        return 'store';
      case '3':
        return 'clinic';
      case '4':
        return 'store and clinic';
      case '5':
        return 'shipping';
      default:
        return 'unknown';
    }
  }

  warehouseTypeIcon(type: any): string {
    switch (String(type)) {
      case '0':
        return 'fa-solid fa-building';
      case '1':
        return 'fa-solid fa-warehouse';
      case '2':
        return 'fa-solid fa-store';
      case '3':
        return 'fa-solid fa-calendar-days';
      case '4':
        return 'fa-solid fa-house-medical';
      case '5':
        return 'fa-solid fa-truck-fast';
      default:
        return 'fa-solid fa-circle-question';
    }
  }

  displayValue(value: any): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string' && value.trim() === '') return '-';
    return String(value);
  }

  getPhoneHref(value: any): string | null {
    if (!value) return null;
    return `tel:${value}`;
  }

  getExternalHref(value: any): string | null {
    if (!value || String(value).trim() === '') return null;

    const link = String(value).trim();

    if (link.startsWith('http://') || link.startsWith('https://')) {
      return link;
    }

    return `https://${link}`;
  }

  formatMoney(value: any): string {
    const numberValue = Number(value || 0);

    return numberValue.toLocaleString(this.isAr ? 'ar-EG' : 'en-US', {
      maximumFractionDigits: 2
    });
  }

  hasTotalValue(value: any): boolean {
    return Number(value || 0) > 0;
  }

  formatDate(date: string): string {
    if (!date) return '-';

    const d = new Date(date);

    if (isNaN(d.getTime())) return '-';

    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(2);

    return `${day}-${month}-${year}`;
  }

  trackById(index: number, item: any): any {
    return item?.id ?? index;
  }
}
